/**
 * @fileoverview Mobile showcase — real Android screenshots displayed
 * inside subtle device frames. On large screens they fan out with
 * alternating tilt so the row reads like a product strip; on small
 * screens they stack vertically. `DeviceShot` is the per-phone
 * presentational helper and only ever used by this section.
 *
 * Extracted verbatim out of the original Landing/index.tsx.
 */
import { Smartphone } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { MOBILE_SHOTS } from '../constants'

export function MobileShowcase() {
  return (
    <section id="mobile" className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-2xl text-center">
        <Badge variant="outline" className="mb-3 gap-1.5">
          <Smartphone className="h-3.5 w-3.5" />
          Built for the field
        </Badge>
        <h2 className="text-balance text-3xl font-bold tracking-tight sm:text-4xl">
          The whole game, in your pocket
        </h2>
        <p className="mt-3 text-muted-foreground">
          Score in real time, follow tournaments, surface stats — all from one
          Android app that the people actually on the ground can rely on.
        </p>
      </div>

      {/* Fan-out row on desktop, stacked column on mobile. The 2/4/6/8 deg
          tilts and slight vertical offsets create a "deck of cards" feel
          without needing custom keyframes. */}
      <div className="mt-14 flex flex-wrap justify-center gap-8 lg:gap-4">
        {MOBILE_SHOTS.map((shot, i) => (
          <DeviceShot
            key={shot.src}
            src={shot.src}
            label={shot.label}
            caption={shot.caption}
            tilt={[-4, 2, -2, 4][i] ?? 0}
            offset={[6, -10, -10, 6][i] ?? 0}
          />
        ))}
      </div>
    </section>
  )
}

interface DeviceShotProps {
  src: string
  label: string
  caption: string
  tilt: number      // rotation in degrees — small numbers feel premium, large ones feel toy
  offset: number    // vertical pixel offset — staggers the row so it doesn't look like a line
}

function DeviceShot({ src, label, caption, tilt, offset }: DeviceShotProps) {
  return (
    <div className="group flex w-full max-w-[15rem] flex-col items-center text-center">
      <div
        className="relative w-full transition-transform duration-300 ease-out group-hover:!translate-y-0 group-hover:!rotate-0"
        style={{ transform: `translateY(${offset}px) rotate(${tilt}deg)` }}
      >
        {/* Soft brand halo behind each phone. */}
        <div className="absolute inset-0 -z-10 scale-105 bg-[radial-gradient(circle_at_center,oklch(0.70_0.18_45/.25),transparent_70%)] blur-2xl" />

        {/* Phone chassis — 8px bezel, rounded corners, speaker bar + lens.
            The image is the "screen" — it already contains the app's
            chrome (status bar, header etc.) so no extra UI is layered. */}
        <div className="relative overflow-hidden rounded-[2rem] border-[8px] border-foreground/90 bg-foreground/90 shadow-xl">
          <div className="absolute left-1/2 top-1.5 z-10 h-1.5 w-12 -translate-x-1/2 rounded-full bg-foreground/40" />
          <div className="absolute right-3 top-1.5 z-10 h-1.5 w-1.5 rounded-full bg-foreground/60" />
          <img
            src={src}
            alt={label}
            className="block h-auto w-full rounded-[1.4rem]"
            loading="lazy"
          />
        </div>
      </div>
      <p className="mt-5 text-sm font-semibold">{label}</p>
      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{caption}</p>
    </div>
  )
}
